import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Anchor, Users, MapPinned, MessageCircle, Facebook, Instagram, Youtube } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useContent, useFetch } from "../../lib/useFetch";
import { EventCard } from "../../components/site/EventCard";
import { fmtDate } from "../../lib/api";
import { PartnersBar } from "./PartnersBar";

import { Img, GEN, classFallback } from "../../components/site/Img";

const HERO = GEN.hero;

const SLIDES = ["hero", "hero_2", "hero_3", "hero_4"];

const Hero = ({ c }) => {
  const { t, trc } = useLang();
  const imgs = SLIDES.map((k) => c.images?.fields?.[k]).filter(Boolean);
  const slides = imgs.length ? imgs : [HERO];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [slides.length]);
  return (
    <section className="grain relative flex min-h-[92vh] items-end overflow-hidden bg-[#0A2540]" data-testid="home-hero">
      {slides.map((src, n) => (
        <Img key={src + n} src={src} fallback={HERO} alt={`Atlantis Academy sailing – slide ${n + 1}`} data-testid={`home-hero-slide-${n}`}
          className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-[1600ms] ease-out ${n === i ? "scale-105 opacity-100" : "scale-100 opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#061a2e]/90 via-[#0A2540]/55 to-transparent" />
      <div className="container-x relative z-10 pb-24 pt-40">
        <p className="eyebrow rise">{t("hero_eyebrow")}</p>
        <h1 className="rise d1 mt-6 max-w-3xl font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">{trc(c, "home", "hero_title")}</h1>
        <p className="rise d2 mt-6 max-w-xl text-base text-white/75 md:text-lg">{trc(c, "home", "hero_subtitle")}</p>
        <div className="rise d3 mt-10 flex flex-wrap gap-4">
          <Link to="/clinics" className="btn-buoy" data-testid="hero-explore-clinics">{t("book_clinic")}<ArrowRight size={16} /></Link>
          <Link to="/contacts" className="btn-ghost-light" data-testid="hero-contact">{t("contact_us")}</Link>
        </div>
        {slides.length > 1 && (
          <div className="mt-12 flex gap-2" data-testid="home-hero-dots">
            {slides.map((_, n) => (
              <button key={n} onClick={() => setI(n)} aria-label={`Slide ${n + 1}`} data-testid={`home-hero-dot-${n}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${n === i ? "w-10 bg-[#FF5722]" : "w-5 bg-white/40 hover:bg-white/70"}`} />
            ))}
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 h-8 w-[200%] wave-track opacity-60">
        <svg viewBox="0 0 1200 30" preserveAspectRatio="none" className="h-full w-full"><path d="M0 15 Q75 0 150 15 T300 15 T450 15 T600 15 T750 15 T900 15 T1050 15 T1200 15 V30 H0Z" fill="#F8FAFC" /></svg>
      </div>
    </section>
  );
};

const Classes = () => {
  const { t, tr } = useLang();
  const { data } = useFetch("/public/classes", { cached: true });
  return (
    <section className="container-x py-24" data-testid="home-classes">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div><p className="eyebrow">Optimist · ILCA · 420 · 29er</p><h2 className="mt-3 font-display text-3xl font-extrabold text-[#0A2540] md:text-5xl">{t("classes_title")}</h2></div>
        <p className="max-w-sm text-slate-500">{t("classes_sub")}</p>
      </div>
      <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {(data || []).map((c, i) => (
          <Link key={c.id} to={`/clinics?class=${encodeURIComponent(c.name)}`} data-testid={`class-tile-${c.name}`}
            className={`card-lift img-zoom group relative overflow-hidden rounded-3xl ${i % 2 ? "lg:mt-12" : ""}`}>
            <Img src={c.image} fallback={classFallback(c.name)} alt={c.name} className="h-72 w-full object-cover md:h-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/20 to-transparent" />
            <div className="absolute bottom-0 p-5 text-white">
              <h3 className="font-display text-3xl font-black tracking-tight md:text-4xl">{c.name}</h3>
              <p className="mt-2 line-clamp-2 text-xs text-white/75 md:text-sm">{tr(c, "description")}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Featured = () => {
  const { t } = useLang();
  const { data } = useFetch("/events?featured=true");
  const evs = (data || []).filter((e) => e.status !== "Completed").sort((a, b) => (b.by_invitation ? 1 : 0) - (a.by_invitation ? 1 : 0)).slice(0, 3);
  return (
    <section className="bg-white py-24" data-testid="home-featured">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="eyebrow">2026 – 2027</p><h2 className="mt-3 font-display text-3xl font-extrabold text-[#0A2540] md:text-5xl">{t("featured_title")}</h2></div>
          <Link to="/clinics" className="flex items-center gap-2 font-semibold text-[#0A2540] hover:text-[#FF5722]" data-testid="home-see-calendar">{t("see_all")}<ArrowRight size={16} /></Link>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">{evs.map((e) => <EventCard key={e.id} e={e} />)}</div>
      </div>
    </section>
  );
};

const Services = ({ c }) => {
  const { t, trc } = useLang();
  const items = [[Users, t("coaching"), "coaching_text"], [Anchor, t("charter"), "charter_text"], [Trophy, t("medals"), "medals_text"], [MapPinned, t("exclusive"), "location_text"]];
  return (
    <section className="relative overflow-hidden bg-[#0A2540] py-24 text-white" data-testid="home-services">
      <div className="container-x grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">{t("services")}</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight md:text-5xl">{t("medals")}</h2>
          <p className="mt-6 text-white/65">{trc(c, "home", "services_text")}</p>
        </div>
        <div className="grid gap-px overflow-hidden rounded-3xl bg-white/10 sm:grid-cols-2 lg:col-span-7">
          {items.map(([Icon, title, key]) => (
            <div key={key} className="bg-[#0A2540] p-8 transition-colors hover:bg-[#0d2f52]">
              <Icon className="text-[#00A8B5]" size={28} />
              <h3 className="mt-5 font-display text-lg font-bold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{trc(c, "home", key)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const News = () => {
  const { t, tr, lang } = useLang();
  const { data } = useFetch("/public/news");
  return (
    <section className="container-x py-24" data-testid="home-news">
      <p className="eyebrow">News</p>
      <h2 className="mt-3 font-display text-3xl font-extrabold text-[#0A2540] md:text-5xl">{t("news_title")}</h2>
      <div className="mt-12 grid gap-8 md:grid-cols-3">
        {(data || []).slice(0, 3).map((n) => (
          <Link key={n.id} to={`/news/${n.id}`} className="img-zoom group" data-testid={`news-card-${n.id}`}>
            <div className="overflow-hidden rounded-3xl bg-slate-100"><Img src={n.image} fallback={HERO} className="h-60 w-full object-cover" /></div>
            <p className="mt-5 text-xs font-bold uppercase tracking-wider text-[#00A8B5]">{fmtDate(n.date, lang)}</p>
            <h3 className="mt-2 font-display text-xl font-bold text-[#0A2540] group-hover:text-[#FF5722]">{tr(n, "title")}</h3>
            <p className="mt-2 line-clamp-2 text-sm text-slate-500">{tr(n, "excerpt")}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Whatsapp = ({ c }) => {
  const { t, trc } = useLang();
  return (
    <section className="container-x pb-24">
      <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-gradient-to-r from-[#00A8B5] to-[#0089a0] p-10 text-white md:flex-row md:items-center">
        <div><h3 className="font-display text-2xl font-extrabold md:text-3xl">{t("whatsapp")}</h3><p className="mt-2 text-white/85">{trc(c, "home", "whatsapp_text")}</p></div>
        <a href={c.site?.fields?.whatsapp_url} target="_blank" rel="noreferrer" className="btn-buoy" data-testid="home-whatsapp-join"><MessageCircle size={16} />{t("join_now")}</a>
      </div>
    </section>
  );
};

const Social = ({ c }) => {
  const { t } = useLang();
  const f = c.site?.fields || {};
  const items = [["facebook", Facebook], ["instagram", Instagram], ["youtube", Youtube]].filter(([k]) => f[`${k}_url`]);
  if (!items.length) return null;
  return (
    <section className="container-x pb-24" data-testid="home-social">
      <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-slate-200 p-10 md:flex-row md:items-center dark:border-white/10">
        <div><p className="eyebrow">Social</p><h3 className="mt-2 font-display text-2xl font-extrabold text-[#0A2540] md:text-3xl dark:text-white">{t("follow_us")}</h3></div>
        <div className="flex gap-3">
          {items.map(([k, Icon]) => (
            <a key={k} href={f[`${k}_url`]} target="_blank" rel="noreferrer" aria-label={k} data-testid={`home-social-${k}`}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0A2540] text-white transition-transform transition-colors hover:-translate-y-1 hover:bg-[#FF5722]"><Icon size={22} /></a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const c = useContent();
  return (
    <div data-testid="home-page">
      <Hero c={c} />
      <Classes />
      <PartnersBar />
      <Featured />
      <Services c={c} />
      <News />
      <Whatsapp c={c} />
      <Social c={c} />
    </div>
  );
}
